import React, { useCallback, useEffect, useState } from 'react';
import {
  Upload, Button, Select, Input, Space, Table, Tag, Tooltip, Popconfirm, message,
  Segmented, Row, Col, Card, Empty, Image, Typography,
} from 'antd';
import {
  UploadOutlined, DownloadOutlined, DeleteOutlined, EyeOutlined, SearchOutlined,
  FileOutlined, FilePdfOutlined, FileImageOutlined, FileExcelOutlined,
  FileWordOutlined, FileZipOutlined, AppstoreOutlined, BarsOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  fetchDocuments, uploadDocuments, deleteDocument, fileUrl, downloadUrl,
} from '../services/documentService';

const { Text } = Typography;

const fmtSize = (b) => {
  if (!b) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
};

const fileIcon = (type = '', name = '') => {
  const t = (type || '').toLowerCase();
  const n = (name || '').toLowerCase();
  if (t.includes('image')) return <FileImageOutlined style={{ color: '#52c41a' }} />;
  if (t.includes('pdf') || n.endsWith('.pdf')) return <FilePdfOutlined style={{ color: '#f5222d' }} />;
  if (t.includes('sheet') || n.match(/\.(xlsx?|csv)$/)) return <FileExcelOutlined style={{ color: '#52c41a' }} />;
  if (t.includes('word') || n.match(/\.docx?$/)) return <FileWordOutlined style={{ color: '#1890ff' }} />;
  if (t.includes('zip') || n.match(/\.(zip|rar|7z)$/)) return <FileZipOutlined style={{ color: '#fa8c16' }} />;
  return <FileOutlined style={{ color: '#8c8c8c' }} />;
};

const isImage = (d) => (d.FileType || '').toLowerCase().includes('image');

// Reusable document manager. Scope it with leadId / clientId, or pass neither
// for the centralized library.
const DocumentManager = ({ leadId, clientId, entityType, settings = {}, compact = false, onChanged }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState();
  const [filterCat, setFilterCat] = useState();
  const [search, setSearch] = useState('');
  const [view, setView] = useState('list');
  const [preview, setPreview] = useState(null);

  const categories = settings.doc_category || [];
  const scope = { LeadID: leadId, ClientID: clientId };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...scope };
      if (filterCat) params.Category = filterCat;
      if (search) params.search = search;
      setDocs(await fetchDocuments(params));
    } catch { message.error('Failed to load documents'); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, clientId, filterCat, search]);

  useEffect(() => { load(); }, [load]);

  const customRequest = async ({ file, onSuccess, onError }) => {
    try {
      await uploadDocuments([file], {
        EntityType: entityType || (clientId ? 'Client' : leadId ? 'Lead' : 'Lead'),
        LeadID: leadId, ClientID: clientId, Category: category,
      });
      onSuccess && onSuccess();
      message.success(`${file.name} uploaded`);
      load(); onChanged && onChanged();
    } catch (e) {
      onError && onError(e);
      message.error(`Failed to upload ${file.name}`);
    }
  };

  const handleDelete = async (d) => {
    try { await deleteDocument(d.DocumentID); message.success('Deleted'); load(); onChanged && onChanged(); }
    catch { message.error('Failed to delete'); }
  };

  const openPreview = (d) => {
    if (isImage(d)) setPreview(d);
    else window.open(fileUrl(d), '_blank');
  };

  const toolbar = (
    <Row gutter={[8, 8]} justify="space-between" style={{ marginBottom: 16 }}>
      <Col>
        <Space wrap>
          <Select placeholder="Upload category" allowClear style={{ width: 170 }}
            value={category} onChange={setCategory}
            options={categories.map(c => ({ value: c.Value, label: c.Label }))} />
          <Upload multiple showUploadList={false} customRequest={customRequest}>
            <Button type="primary" icon={<UploadOutlined />}>Upload</Button>
          </Upload>
        </Space>
      </Col>
      <Col>
        <Space wrap>
          <Input.Search placeholder="Search files" allowClear style={{ width: 180 }}
            enterButton={<SearchOutlined />} onSearch={setSearch}
            onChange={(e) => { if (!e.target.value) setSearch(''); }} />
          <Select placeholder="Category" allowClear style={{ width: 150 }}
            value={filterCat} onChange={setFilterCat}
            options={categories.map(c => ({ value: c.Value, label: c.Label }))} />
          <Segmented value={view} onChange={setView}
            options={[{ value: 'list', icon: <BarsOutlined /> }, { value: 'grid', icon: <AppstoreOutlined /> }]} />
        </Space>
      </Col>
    </Row>
  );

  const actions = (d) => (
    <Space size={0}>
      <Tooltip title="Preview"><Button type="text" size="small" icon={<EyeOutlined />} onClick={() => openPreview(d)} /></Tooltip>
      <Tooltip title="Download"><Button type="text" size="small" icon={<DownloadOutlined />} href={downloadUrl(d)} /></Tooltip>
      <Popconfirm title="Delete this document?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => handleDelete(d)}>
        <Button type="text" size="small" danger icon={<DeleteOutlined />} />
      </Popconfirm>
    </Space>
  );

  const listView = (
    <Table
      size="small" bordered rowKey="DocumentID" loading={loading} dataSource={docs}
      locale={{ emptyText: <Empty image={<FileOutlined style={{ fontSize: 40, color: '#d9d9d9' }} />} description="No documents yet" /> }}
      pagination={docs.length > 10 ? { pageSize: 10 } : false}
      columns={[
        { title: 'Name', dataIndex: 'OriginalName', render: (t, d) => <Space>{fileIcon(d.FileType, t)}<Text>{t}</Text>{d.Version > 1 && <Tag>v{d.Version}</Tag>}</Space> },
        { title: 'Category', dataIndex: 'Category', width: 140, render: (c) => c ? <Tag>{c}</Tag> : '—' },
        { title: 'Size', dataIndex: 'FileSize', width: 90, render: fmtSize },
        { title: 'Uploaded', dataIndex: 'createdAt', width: 150, render: (d) => dayjs(d).format('DD MMM, hh:mm A') },
        { title: 'Actions', key: 'a', width: 130, align: 'center', render: (_, d) => actions(d) },
      ]}
    />
  );

  const gridView = (
    docs.length === 0 ? <Empty description="No documents yet" /> : (
      <Row gutter={[12, 12]}>
        {docs.map(d => (
          <Col xs={12} sm={8} md={6} lg={4} key={d.DocumentID}>
            <Card size="small" hoverable bodyStyle={{ padding: 12 }}
              cover={isImage(d)
                ? <div style={{ height: 110, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>
                    <img alt={d.OriginalName} src={fileUrl(d)} style={{ maxHeight: 110, maxWidth: '100%' }} /></div>
                : <div style={{ height: 110, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa', fontSize: 40 }}>{fileIcon(d.FileType, d.OriginalName)}</div>}
              actions={[
                <EyeOutlined key="v" onClick={() => openPreview(d)} />,
                <DownloadOutlined key="d" onClick={() => { window.location.href = downloadUrl(d); }} />,
                <Popconfirm key="x" title="Delete?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => handleDelete(d)}><DeleteOutlined /></Popconfirm>,
              ]}>
              <Card.Meta
                title={<Tooltip title={d.OriginalName}><Text ellipsis style={{ fontSize: 12 }}>{d.OriginalName}</Text></Tooltip>}
                description={<Text type="secondary" style={{ fontSize: 11 }}>{fmtSize(d.FileSize)}{d.Category ? ` · ${d.Category}` : ''}</Text>}
              />
            </Card>
          </Col>
        ))}
      </Row>
    )
  );

  return (
    <div>
      {toolbar}
      {view === 'list' ? listView : gridView}
      {preview && (
        <Image
          style={{ display: 'none' }}
          preview={{ visible: true, src: fileUrl(preview), onVisibleChange: (v) => { if (!v) setPreview(null); } }}
        />
      )}
    </div>
  );
};

export default DocumentManager;
